// src/components/manuscriptTheme/ManuscriptThemeMenu.jsx
//
// The theme switcher behind the button in the editor's format bar: the presets,
// then the author's own saved themes, then "Customize theme…". It replaces the
// prototype's dropdown in the right rail, which the ticket removed.
//
// Picking one applies it to the manuscript straight away, as the prototype's
// picker does. What applying means — the copy on the draft, the offer to clear
// old formatting — is the editor's; this only reports the choice.
import React, { useEffect, useState } from 'react';
import { Box, Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from '../../theme';
import { PRESETS } from '../../config/manuscriptThemes';
import { fontStackFor } from '../../config/fonts';
import { listSavedThemes } from '../../utils/manuscriptThemeApi';

function Swatch({ theme }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 38, height: 38, flexShrink: 0,
        borderRadius: `${tokens.radius.panel}px`,
        border: `1px solid ${tokens.color.line}`,
        display: 'grid', placeItems: 'center', fontSize: 19,
        // The theme's own paper, accent and title face: data, not chrome.
        background: theme.col.paper,
        color: theme.col.accent,
        fontFamily: fontStackFor(theme.ch.tFont),
        fontStyle: theme.ch.tItalic ? 'italic' : 'normal',
        fontWeight: theme.ch.tWeight,
      }}
    >
      Aa
    </Box>
  );
}

// The app's menus are purple (its MUI theme), so everything here is read on a
// dark ground.
const subheaderSx = {
  fontFamily: tokens.family.sans,
  fontSize: tokens.text.micro.size,
  fontWeight: 700,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.color.onDark,
  background: 'transparent',
  lineHeight: '28px',
};

export default function ManuscriptThemeMenu({
  anchorEl,
  onClose,
  currentSourceId,
  hasTheme = false,
  onPick,
  onCustomize,
  onRemove,
}) {
  const open = Boolean(anchorEl);
  const [saved, setSaved] = useState([]);

  // Read fresh each time it opens: a theme saved on the Theme Editor page a
  // moment ago should be here without a reload.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    listSavedThemes()
      .then((list) => { if (!cancelled && Array.isArray(list)) setSaved(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const item = (theme, sourceId, blurb) => {
    const current = currentSourceId === sourceId;
    return (
      <MenuItem
        key={sourceId}
        selected={current}
        onClick={() => { onClose(); onPick(theme, { name: theme.name, sourceId }); }}
        sx={{ gap: 1.5, py: 1, alignItems: 'center' }}
      >
        <Swatch theme={theme} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 14, color: tokens.color.card }}>{theme.name}</Typography>
          <Typography noWrap sx={{ fontSize: 12, color: tokens.color.onDark, maxWidth: 230 }}>{blurb}</Typography>
        </Box>
        {current && <CheckIcon fontSize="small" sx={{ color: tokens.color.goldLight }} />}
      </MenuItem>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 340, maxHeight: '70vh' } } }}
    >
      <ListSubheader sx={subheaderSx}>Presets</ListSubheader>
      {PRESETS.map((p) => item(p, p.id, p.blurb))}
      {saved.length > 0 && <ListSubheader sx={subheaderSx}>Your themes</ListSubheader>}
      {saved.map((t) => item(t, t.id, 'Your custom theme'))}
      <Divider />
      <MenuItem onClick={() => { onClose(); onCustomize(); }} sx={{ fontSize: 14 }}>
        Customize theme…
      </MenuItem>
      {hasTheme && (
        <MenuItem onClick={() => { onClose(); onRemove(); }} sx={{ fontSize: 14, color: tokens.color.onDark }}>
          Remove theme — back to the manuscript’s own formatting
        </MenuItem>
      )}
    </Menu>
  );
}
